/**
 * Manage Integration dialog.
 *
 * Lets an admin:
 *   - See the masked credential hint (confirm "which key is live")
 *   - Test the connection (calls the adapter's test_connection)
 *   - Rotate the API key in place
 *   - Toggle is_active off (soft-retire without deleting)
 *   - Disconnect (soft-delete) - confirmation required
 *
 * Credential rotation goes through PATCH /api/integrations/<uuid>/, the
 * same as toggling active. Rotating does NOT invalidate the adapter cache;
 * at 24h TTL that's acceptable - documented in the backend plan.
 */

import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  useDisconnectIntegration,
  useIntegration,
  useTestIntegrationConnection,
  useUpdateIntegration,
} from '@/admin/hooks/use-integrations'

import { toastApiError } from '@/shared/lib/api-error'
interface ManageIntegrationDialogProps {
  uuid: string
  open: boolean
  onClose: () => void
}

type TestState = 'idle' | 'testing' | 'healthy' | 'unhealthy'

/**
 * Public wrapper. Keyed by ``uuid`` so React remounts the inner dialog body
 * whenever the caller swaps to a different integration - cleanly resets all
 * local state without a setState-in-effect pattern.
 */
export function ManageIntegrationDialog(props: ManageIntegrationDialogProps) {
  return <ManageIntegrationDialogInner key={props.uuid} {...props} />
}

function ManageIntegrationDialogInner({
  uuid,
  open,
  onClose,
}: ManageIntegrationDialogProps) {
  const { data: integration, isLoading } = useIntegration(uuid)
  const update = useUpdateIntegration(uuid)
  const disconnect = useDisconnectIntegration()
  const testConnection = useTestIntegrationConnection()

  const [newApiKey, setNewApiKey] = useState('')
  const [testState, setTestState] = useState<TestState>('idle')
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)

  async function handleTest() {
    setTestState('testing')
    try {
      const result = await testConnection.mutateAsync(uuid)
      setTestState(result.healthy ? 'healthy' : 'unhealthy')
    } catch {
      setTestState('unhealthy')
    }
  }

  async function handleRotate() {
    if (!newApiKey.trim()) return
    try {
      await update.mutateAsync({ credentials: { api_key: newApiKey.trim() } })
      toast.success('API key updated')
      setNewApiKey('')
      setTestState('idle')
    } catch (err) {
      toastApiError(err, 'Failed to update API key');
    }
  }

  async function handleToggleActive() {
    if (!integration) return
    const willEnable = !integration.is_active
    try {
      const result = await update.mutateAsync({ is_active: willEnable })
      const paused = result.deactivated_providers ?? []
      if (willEnable && paused.length > 0) {
        // Backend paused a sibling to keep one-active-per-category holding.
        toast.success(
          `Integration enabled · ${paused.join(', ')} paused`,
        )
      } else {
        toast.success(willEnable ? 'Integration enabled' : 'Integration paused')
      }
    } catch (err) {
      toastApiError(err, 'Failed to update integration');
    }
  }

  async function handleDisconnect() {
    try {
      await disconnect.mutateAsync(uuid)
      toast.success('Integration disconnected')
      onClose()
    } catch (err) {
      toastApiError(err, 'Failed to disconnect');
    }
  }

  function handleClose() {
    setNewApiKey('')
    setTestState('idle')
    setConfirmDisconnect(false)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {integration ? providerLabel(integration.provider) : 'Integration'}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            Manage credentials and connection status for this provider.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !integration ? (
          <div className="flex items-center gap-2 py-6 text-[13px] text-[var(--ink-3)]">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="space-y-5">
            {/* Current key + status */}
            <section className="space-y-2">
              <Label className="block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Current API Key
              </Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2 font-mono text-[12px] text-[var(--ink-2)]">
                  {integration.credentials_hint || 'No key stored'}
                </code>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleTest}
                  disabled={testState === 'testing'}
                >
                  {testState === 'testing' && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                  Test
                </Button>
              </div>

              <TestResult state={testState} />

              <div className="flex items-center justify-between pt-2">
                <div className="flex items-center gap-2 text-[11px] text-[var(--ink-3)]">
                  <span
                    className={
                      integration.is_active
                        ? 'inline-block h-1.5 w-1.5 rounded-full bg-[var(--green)]'
                        : 'inline-block h-1.5 w-1.5 rounded-full bg-[var(--ink-3)]'
                    }
                  />
                  {integration.is_active ? 'Active' : 'Paused'}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleToggleActive}
                  disabled={update.isPending}
                >
                  {integration.is_active ? 'Pause' : 'Enable'}
                </Button>
              </div>
            </section>

            {/* Rotate */}
            <section className="border-t-[0.5px] border-t-[var(--rule)] pt-5">
              <Label className="mb-2 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                Replace API Key
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="password"
                  placeholder="Paste a new API key"
                  autoComplete="off"
                  spellCheck={false}
                  value={newApiKey}
                  onChange={(event) => setNewApiKey(event.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleRotate}
                  disabled={!newApiKey.trim() || update.isPending}
                >
                  {update.isPending && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                  Save
                </Button>
              </div>
              <p className="mt-2 text-[11px] text-[var(--ink-3)]">
                Replacing the key takes effect immediately. Cached search
                results may use the old key for up to 24 hours.
              </p>
            </section>

            {/* Disconnect */}
            <section className="border-t-[0.5px] border-t-[var(--rule)] pt-5">
              {!confirmDisconnect ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setConfirmDisconnect(true)}
                >
                  Disconnect
                </Button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start gap-2 rounded-[4px] border-[0.5px] border-[var(--status-warn-bg)] bg-[var(--status-warn-bg)] px-3 py-2 text-[12px] text-[var(--status-warn)]">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      Disconnecting removes this credential. Your organisation
                      will fall back to the platform default, if configured.
                    </span>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmDisconnect(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={handleDisconnect}
                      disabled={disconnect.isPending}
                    >
                      {disconnect.isPending && (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      )}
                      Yes, disconnect
                    </Button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
          <Button type="button" variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Small view helpers
// ---------------------------------------------------------------------------

function TestResult({ state }: { state: TestState }) {
  if (state === 'idle') return null
  if (state === 'testing') {
    return (
      <p className="flex items-center gap-1.5 text-[11px] text-[var(--ink-3)]">
        <Loader2 className="h-3 w-3 animate-spin" />
        Testing connection…
      </p>
    )
  }
  if (state === 'healthy') {
    return (
      <p className="flex items-center gap-1.5 text-[11px] text-[var(--green)]">
        <CheckCircle2 className="h-3 w-3" />
        Connection successful.
      </p>
    )
  }
  return (
    <p className="flex items-center gap-1.5 text-[11px] text-[var(--status-error)]">
      <XCircle className="h-3 w-3" />
      Connection failed. Check the key.
    </p>
  )
}

function providerLabel(provider: string): string {
  switch (provider) {
    case 'goaddress':
      return 'GoAddress'
    case 'portalgo':
      return 'PortalGo Address'
    case 'fetchify':
      return 'Fetchify'
    case 'dvla':
      return 'DVLA'
    default:
      return provider
  }
}
